import { NucleusVaultMetadata } from '@/constants/nucleus-vaults';
import { NucleusClient } from '@/network/clients/nucleus';
import { capitalizeFirstLetter, getBlockExplorerUrlFromTxHash } from '@/utils';
import { Box, Card, Circle, FormatNumber, Heading, HStack, Link, Table, Text } from '@chakra-ui/react';
import { useAppKitAccount } from '@reown/appkit/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { erc20Abi, formatUnits } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';
import { TNucleusDeposit, TNucleusWithdrawal } from './types';

type Transaction = {
  type: 'Withdrawal' | 'Deposit';
  time: number;
  hash: string;
  status: 'success' | 'pending' | 'failed';
  vaultTokenAmount: bigint;
  userTokenAmount: bigint;
  userTokenAddress: string;
};

const client = new NucleusClient();

const withdrawStatusToStatus = (status: string) => {
  if (status.toLowerCase() === 'fulfilled') return 'success';
  if (status.toLowerCase() === 'cancelled') return 'failed';
  return 'pending';
};
export const VaultHistory = ({ vaultMetadata }: { vaultMetadata: NucleusVaultMetadata }) => {
  const { vaultAddress, vaultKey } = vaultMetadata;
  const { address } = useAppKitAccount();
  const { data: transactionHistory } = useQuery({
    queryKey: ['transactionHistory', vaultAddress, address],
    queryFn: async (): Promise<Transaction[]> => {
      if (!address || !vaultAddress) return [];
      const withdrawalsResponse: TNucleusWithdrawal[] = await client.get(
        '/protocol/withdrawals',
        {
          user: address,
          vaultAddress,
          status: 'all',
          all: true,
        },
        false,
      );
      const depositsResponse: TNucleusDeposit[] = await client.get('/vaults/deposits', {
        user: address,
        vaultAddress,
      });
      const withdrawals: Transaction[] = withdrawalsResponse?.map((w) => ({
        type: 'Withdrawal' as const,
        hash: w.created_transaction_hash,
        time: Number(w.created_timestamp) * 1000,
        vaultTokenAmount: BigInt(w.offer_amount_spent),
        userTokenAmount: BigInt(w.want_amount_rec),
        userTokenAddress: w.want_token.toLowerCase(),
        status: withdrawStatusToStatus(w.status),
      }));
      const deposits: Transaction[] = depositsResponse.map((d) => ({
        type: 'Deposit' as const,
        hash: d.tx_hash,
        time: Number(d.time) * 1000,
        vaultTokenAmount: BigInt(d.share_amount),
        userTokenAmount: BigInt(d.asset_amount),
        userTokenAddress: d.asset.toLowerCase(),
        status: 'success',
      }));
      const transactions = deposits.concat(withdrawals).sort((a, b) => b.time - a.time);
      return transactions;
    },
  });
  const { data: vaultTokenDecimals } = useReadContract({
    abi: erc20Abi,
    address: vaultAddress,
    functionName: 'decimals',
  });

  const userTokenAddresses = useMemo(
    () => [...new Set(transactionHistory?.map((tx) => tx.userTokenAddress.toLowerCase()))],
    [transactionHistory],
  );

  const { data: decimalsResults } = useReadContracts({
    contracts: userTokenAddresses.map((address) => ({
      address: address as EthAddress,
      abi: erc20Abi,
      functionName: 'decimals' as const,
    })),
  });

  const { data: symbolResults } = useReadContracts({
    contracts: userTokenAddresses.map((address) => ({
      address: address as EthAddress,
      abi: erc20Abi,
      functionName: 'symbol' as const,
    })),
  });

  const tokenMetaMap = useMemo(
    () =>
      userTokenAddresses.reduce<Record<string, { decimals: number; symbol: string }>>((acc, address, i) => {
        const decimals = decimalsResults?.[i]?.result;
        const symbol = symbolResults?.[i]?.result;

        if (decimals != null && symbol != null) {
          acc[address] = {
            decimals: Number(decimals),
            symbol: String(symbol),
          };
        }

        return acc;
      }, {}),
    [decimalsResults, symbolResults, userTokenAddresses],
  );
  if (!transactionHistory?.length) {
    return null;
  }

  return (
    <Box>
      <Heading size="2xl" fontWeight="700" mb={4}>
        Your transactions
      </Heading>
      <Card.Root borderRadius="2xl">
        <Card.Body>
          <Box maxH={300} overflow="auto">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Type</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Date & Time</Table.ColumnHeader>
                  <Table.ColumnHeader>Amount</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {transactionHistory?.map((transaction) => {
                  const [date, year, time] = new Date(transaction.time)
                    .toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true, // 12-hour format with AM/PM
                      timeZoneName: 'short',
                    })
                    .split(',');

                  const { decimals, symbol } = tokenMetaMap[transaction.userTokenAddress.toLowerCase()] ?? {};
                  return (
                    <Table.Row key={transaction.hash} fontSize="md" fontWeight="500">
                      <Table.Cell>
                        <Link href={getBlockExplorerUrlFromTxHash(transaction.hash)} target="_blank">
                          {transaction.type}
                          <FiArrowUpRight />
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack>
                          <StatusCircle status={transaction.status} />
                          {capitalizeFirstLetter(transaction.status)}
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>
                        {date},{year}
                        <Text fontSize="sm" color="fg.subtle">
                          {time}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Box>
                          <Text>
                            <FormatNumber
                              value={Number(formatUnits(transaction?.vaultTokenAmount ?? 0, vaultTokenDecimals ?? 0))}
                              maximumFractionDigits={2}
                            />{' '}
                            {vaultKey}
                          </Text>
                          <Text fontSize="sm" color="fg.subtle">
                            <FormatNumber
                              value={Number(formatUnits(transaction?.userTokenAmount ?? 0, decimals ?? 0))}
                              maximumFractionDigits={2}
                            />{' '}
                            {symbol}
                          </Text>
                        </Box>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        </Card.Body>
      </Card.Root>
    </Box>
  );
};

const StatusCircle = ({ status }: { status: 'success' | 'pending' | 'failed' }) => {
  const getColorForStatus = () => {
    if (status === 'success') return '#108258';
    if (status === 'pending') return 'fg.muted';
    if (status === 'failed') return 'red.100';
  };
  return <Circle size={2} bgColor={getColorForStatus()} />;
};
